import {
  FileTypes,
  ReportDetailsDocument,
  ReportDetailsQuery,
  useAddAndRemoveFilesMutation,
} from '@/__generated__/graphql';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Box } from '@mui/material';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { downloadFile, urlToBase64 } from '@/utils';
import { useCallback, useEffect, useState } from 'react';
import * as state from '@/components/Analysis/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { Image } from '@/components/Analysis/shared/Image';
import { useAtomValue } from 'jotai';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';

type Props = {
  images: ReportDetailsQuery['report']['files'];
};

const ReportImage = ({
  file,
  setAttachments = () => { },
}: {
  file: Attachment;
  setAttachments?: React.Dispatch<React.SetStateAction<Attachment[]>>;
}) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (hasError) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [hasError]
  );

  const handleError = useCallback(
    (success: boolean) => {
      setHasError(!success);

      if (!success) {
        setAttachments((previous) => {
          return previous.filter((attachment) => attachment.name !== file.name);
        });
      }
    },
    [file.name, setAttachments]
  );
  return (
    <Image
      onClick={handleClick}
      src={file.url ?? ''}
      sx={{
        width: '100%',
        cursor: hasError ? 'not-allowed' : 'pointer',
        pointerEvents: hasError ? 'all' : 'auto',
      }}
      alt={file.name}
      onLoadEnd={handleError}
    />
  );
};

export const ReportImagesGallery = ({ images }: Props) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [validAttachments, setValidAttachments] = useState<Attachment[]>(attachments ?? []);
  const [removeImageFromReport] = useAddAndRemoveFilesMutation({
    refetchQueries: [ReportDetailsDocument],
  });
  const { showSnackBar } = useSnackBarMessage({});
  const report = useAtomValue(state.selectedReport);

  useEffect(() => {
    setValidAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    Promise.all(
      (images || []).map(async ({ fileName, resourcePath, type, id }) => {
        const poiAttachmentUrl = getCloudfrontUrl(resourcePath);
        const isImage =
          type.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes);
        const base64 = isImage ? await urlToBase64(poiAttachmentUrl) : undefined;
        return {
          id: id || '',
          name: fileName,
          base64: base64,
          type,
          isNew: false,
          url: poiAttachmentUrl,
        };
      })
    ).then((response) => {
      setAttachments(response);
    });
  }, [images]);

  const handleRemoveAttachment = async (index: number) => {
    const fileId = attachments[index]?.id;

    if (!fileId) {
      showSnackBar('Attachment ID not found', 'error');
      return;
    }

    try {
      await removeImageFromReport({
        variables: {
          input: {
            collectionName: 'report',
            referenceId: report?.id || '',
            addFiles: [],
            removeFileIds: [fileId],
          },
        },
      });
    } catch (error) {
      const errorMessage = extractErrorMessageFromUnknownError(error);
      showSnackBar(errorMessage, 'error');
    }
  };

  const handleDownloadAttachment = (index: number) => {
    const currentAttachment = attachments[index];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          width: '100%',
        }}
      >
        <MediaViewer
          mediaFiles={validAttachments}
          handleRemoveAttachment={handleRemoveAttachment}
          handleDownloadAttachment={handleDownloadAttachment}
        >
          {attachments?.map((file) => (
            <Box
              key={file.id}
              sx={{
                flex: '0 0 auto',
                width: '20%',
                padding: 1,
                boxSizing: 'border-box',
              }}
            >
              <ReportImage file={file} setAttachments={setValidAttachments} />
            </Box>
          ))}
        </MediaViewer>
      </Box>
    </Box>
  );
};
